import type { CameraPose } from "@workspace/blocks";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@workspace/blocks/ui";
import { ArrowLeft, RotateCcw, Save } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { trpc } from "../../../lib/trpc";
import { useChunkedUpload } from "../../hooks/useChunkedUpload";
import { useTranslation } from "../../i18n";
import { parseDescriptionWithMetadata } from "../../utils/splatMetadata";
import { GaussianViewer } from "../GaussianViewer";
import { Alert, Button, Select } from "../ui";
import type { UploadRow } from "./types";

type Draft = {
	model: File;
	json?: File;
	bin?: File;
	src: string;
	collision?: { jsonUrl: string; binUrl: string };
	camera: CameraPose & { fov: number };
};
const release = (draft: Draft | null) => {
	if (!draft) return;
	URL.revokeObjectURL(draft.src);
	if (draft.collision) {
		URL.revokeObjectURL(draft.collision.jsonUrl);
		URL.revokeObjectURL(draft.collision.binUrl);
	}
};

export function SplatEditorDialog({
	upload,
	onClose,
	onSaved,
}: {
	upload: UploadRow;
	onClose: () => void;
	onSaved: (upload: UploadRow) => void;
}) {
	const { locale } = useTranslation();
	const ja = locale === "ja";
	const [session] = useState(() => crypto.randomUUID());
	const iframe = useRef<HTMLIFrameElement>(null);
	const initiated = useRef(false);
	const draftRef = useRef<Draft | null>(null);
	const [ready, setReady] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const [dirty, setDirty] = useState(false);
	const [busy, setBusy] = useState(false);
	const [stage, setStage] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [draft, setDraft] = useState<Draft | null>(null);
	const [preview, setPreview] = useState(false);
	const [previewReady, setPreviewReady] = useState(false);
	const [previewFailed, setPreviewFailed] = useState(false);
	const [collision, setCollision] = useState(!!parseDescriptionWithMetadata(upload.description).metadata?.collision);
	const [resolution, setResolution] = useState("0.1");
	const editing = trpc.library.getForEditing.useQuery({ id: upload.id }, { retry: false, refetchOnWindowFocus: false });
	const save = trpc.library.commitModelEdit.useMutation({ networkMode: "always" });
	const utils = trpc.useUtils();
	const uploader = useChunkedUpload();
	const send = useCallback(
		(type: string, data: Record<string, unknown> = {}) => {
			iframe.current?.contentWindow?.postMessage(
				{ channel: "gsplat-editor", session, type, ...data },
				window.location.origin,
			);
		},
		[session],
	);
	useEffect(() => {
		if (!ready || !editing.data || editing.isFetching || initiated.current) return;
		initiated.current = true;
		send("load", {
			url: editing.data.editorUrl,
			filename: `scene.${editing.data.modelType}`,
			camera: parseDescriptionWithMetadata(editing.data.description).metadata?.camera,
		});
	}, [ready, editing.data, editing.isFetching, send]);
	useEffect(() => {
		const receive = (event: MessageEvent) => {
			const data = event.data;
			if (
				event.origin !== window.location.origin ||
				event.source !== iframe.current?.contentWindow ||
				data?.channel !== "gsplat-editor" ||
				data.session !== session
			)
				return;
			if (data.type === "ready") setReady(true);
			if (data.type === "loaded") setLoaded(true);
			if (data.type === "dirty") setDirty(true);
			if (data.type === "progress")
				setStage(
					data.stage === "collision"
						? ja
							? "当たり判定を生成中…"
							: "Building collision…"
						: ja
							? "モデルを書き出し中…"
							: "Exporting model…",
				);
			if (data.type === "error") {
				setError(String(data.message));
				setBusy(false);
			}
			if (
				data.type === "exported" &&
				data.model instanceof ArrayBuffer &&
				data.camera?.position?.length === 3 &&
				data.camera?.target?.length === 3
			) {
				const model = new File([data.model], "edited.spz", { type: "application/octet-stream" });
				const json =
					data.json instanceof ArrayBuffer
						? new File([data.json], "edited.voxel.json", { type: "application/json" })
						: undefined;
				const bin =
					data.bin instanceof ArrayBuffer
						? new File([data.bin], "edited.voxel.bin", { type: "application/octet-stream" })
						: undefined;
				release(draftRef.current);
				const next = {
					model,
					json,
					bin,
					src: URL.createObjectURL(model),
					camera: data.camera,
					...(json && bin
						? { collision: { jsonUrl: URL.createObjectURL(json), binUrl: URL.createObjectURL(bin) } }
						: {}),
				};
				draftRef.current = next;
				setDraft(next);
				setPreviewReady(false);
				setPreviewFailed(false);
				setPreview(true);
				setBusy(false);
				setStage("");
			}
		};
		window.addEventListener("message", receive);
		return () => window.removeEventListener("message", receive);
	}, [session, ja]);
	useEffect(() => () => release(draftRef.current), []);
	useEffect(() => {
		if (!dirty && !busy) return;
		const prevent = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = "";
		};
		window.addEventListener("beforeunload", prevent);
		return () => window.removeEventListener("beforeunload", prevent);
	}, [dirty, busy]);
	useEffect(() => {
		if (ready) return;
		const timeout = setTimeout(
			() =>
				setError(
					ja
						? "エディターが起動できませんでした。WebGPUに対応する最新のChrome・Edge等でお試しください。"
						: "The editor did not start. Try a current browser with WebGPU support.",
				),
			45000,
		);
		return () => clearTimeout(timeout);
	}, [ready, ja]);
	const close = () => {
		if (busy) return;
		if (dirty && !window.confirm(ja ? "3Dモデルの変更を破棄して閉じますか？" : "Discard the 3D changes and close?"))
			return;
		onClose();
	};
	const exportDraft = () => {
		setError(null);
		setBusy(true);
		setStage(ja ? "モデルを書き出し中…" : "Exporting model…");
		send("export", { collision, resolution: Number(resolution) });
	};
	const commit = async () => {
		if (!draft || !editing.data || busy || previewFailed || !previewReady) return;
		setError(null);
		setBusy(true);
		try {
			const put = async (file: File, kind: "model" | "json" | "bin") => {
				setStage(ja ? `${kind === "model" ? "モデル" : "当たり判定"}をアップロード中…` : `Uploading ${kind}…`);
				const result = await uploader.uploadFile(file, undefined, { id: upload.id, kind });
				if (!result.success || !result.fileUrl) throw new Error(result.error || "Upload failed");
				return result.fileUrl;
			};
			const modelFileUrl = await put(draft.model, "model");
			let files: { jsonUrl: string; binUrl: string } | null = null;
			if (draft.json && draft.bin)
				files = { jsonUrl: await put(draft.json, "json"), binUrl: await put(draft.bin, "bin") };
			setStage(ja ? "共有リンクを更新中…" : "Updating the shared model…");
			const updated = await save.mutateAsync({
				id: upload.id,
				revision: editing.data.revision,
				modelFileUrl,
				camera: draft.camera,
				collision: files,
			});
			send("committed");
			setDirty(false);
			await Promise.all([
				utils.library.listMine.invalidate(),
				utils.library.summary.invalidate(),
				utils.library.getForEditing.invalidate({ id: upload.id }),
				utils.viewer.getGaussianSplat.invalidate({ shareId: upload.shareId }),
			]);
			onSaved(updated);
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : String(cause));
		} finally {
			setBusy(false);
		}
	};
	return (
		<Dialog
			open
			onOpenChange={open => {
				if (!open) close();
			}}
		>
			<DialogContent
				hideClose
				className="account-surface account-editor-dialog flex h-dvh max-h-dvh w-screen max-w-none flex-col gap-0 rounded-none border-0 p-0 sm:max-w-none"
				onPointerDownOutside={event => event.preventDefault()}
				onEscapeKeyDown={event => {
					event.preventDefault();
					close();
				}}
			>
				<header className="account-tool-header shrink-0">
					<div className="account-tool-title min-w-0">
						<DialogTitle className="text-base">
							{preview
								? ja
									? "保存前の確認"
									: "Review before saving"
								: ja
									? "SuperSplatで3Dモデルを編集"
									: "Edit 3D in SuperSplat"}
						</DialogTitle>
						<DialogDescription className="truncate text-xs">
							{upload.title || upload.shareId} · SuperSplat 3.5.2
						</DialogDescription>
					</div>
					<div className="account-tool-actions">
						<Button
							variant="secondary"
							disabled={busy}
							onClick={
								preview
									? () => {
											setPreview(false);
											setError(null);
										}
									: close
							}
						>
							<ArrowLeft size={16} />
							{preview ? (ja ? "編集に戻る" : "Back to editor") : ja ? "閉じる" : "Close"}
						</Button>
						<Button
							disabled={busy || !loaded || (preview && (!previewReady || previewFailed))}
							onClick={preview ? () => void commit() : exportDraft}
						>
							<Save size={16} />
							{preview ? (ja ? "3Dモデルを更新" : "Update this model") : ja ? "変更を確認" : "Review changes"}
						</Button>
					</div>
				</header>
				{!preview && (
					<div className="account-tool-toolbar shrink-0">
						<fieldset className="account-control-group" aria-label={ja ? "全体を180°回転" : "Rotate all 180°"}>
							<span className="account-tool-label">{ja ? "全体を180°回転" : "Rotate all 180°"}</span>
							<div className="account-axis-controls">
								{(["x", "y", "z"] as const).map(axis => (
									<Button
										key={axis}
										variant="secondary"
										size="sm"
										disabled={!loaded || busy}
										onClick={() => send("rotate", { axis })}
									>
										{axis.toUpperCase()}
									</Button>
								))}
							</div>
							<Button variant="ghost" size="sm" disabled={!loaded || busy} onClick={() => send("undo")}>
								<RotateCcw size={14} />
								{ja ? "元に戻す" : "Undo"}
							</Button>
						</fieldset>
						<div className="account-control-group">
							<label className="account-check-field">
								<input
									type="checkbox"
									checked={collision}
									disabled={busy}
									onChange={event => setCollision(event.target.checked)}
								/>
								{ja ? "当たり判定を作り直す" : "Rebuild collision"}
							</label>
							{collision && (
								<Select
									className="w-auto"
									aria-label={ja ? "当たり判定の細かさ" : "Collision resolution"}
									value={resolution}
									disabled={busy}
									onChange={event => setResolution(event.target.value)}
								>
									<option value="0.05">5 cm</option>
									<option value="0.1">10 cm</option>
									<option value="0.2">20 cm</option>
								</Select>
							)}
						</div>
					</div>
				)}
				<output
					className="account-tool-status shrink-0"
					aria-live="polite"
					data-active={busy || (!loaded && !preview && !error) || undefined}
				>
					{busy
						? `${stage}${uploader.isUploading ? ` ${uploader.progress?.percentage ?? 0}%` : ""}`
						: !loaded && !preview
							? error
								? ja
									? "エディターを開けませんでした。閉じてからもう一度お試しください。"
									: "The editor could not open. Close it and try again."
								: ja
									? "エディターを読み込み中…"
									: "Loading the editor…"
							: preview
								? ja
									? "向きと歩行を確認してから更新してください。共有URLと公開設定は維持されます。"
									: "Check orientation and walking, then update. The URL and sharing settings stay the same."
								: ja
									? "選択・移動・回転で微調整できます。表示中の点をSPZで保存します。当たり判定を作り直さない場合、古い判定は解除されます。"
									: "Select, move and rotate to fine-tune. Visible splats are saved as SPZ. Skipping collision removes the old collider."}
				</output>
				{(error || editing.error) && (
					<div className="shrink-0 px-4 py-2">
						<Alert tone="error">{error || editing.error?.message}</Alert>
					</div>
				)}
				<div
					className={
						preview ? "invisible absolute inset-0 pointer-events-none" : "account-tool-canvas relative min-h-0 flex-1"
					}
				>
					<iframe
						ref={iframe}
						title="SuperSplat Editor"
						src={`/editor-assets/supersplat/index.html#session=${session}`}
						className="h-full w-full border-0"
						allow="fullscreen; clipboard-write"
					/>
					{busy && <div className="absolute inset-0 bg-background/30" aria-hidden="true" />}
				</div>
				{preview && draft && (
					<div className="account-tool-canvas relative min-h-0 flex-1">
						<GaussianViewer
							format="spz"
							src={draft.src}
							initialCameraPose={draft.camera}
							centerMode="origin"
							collision={draft.collision}
							onAssetProgress={progress => {
								if (progress >= 1) setPreviewReady(true);
							}}
							onError={failure => {
								setPreviewFailed(true);
								setError(failure.message);
							}}
						/>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
